import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Progress } from './components/ui/progress';
import { Alert, AlertDescription } from './components/ui/alert';
import { useAuth } from './contexts/AuthContext';
import { getApiUrl } from './config';
import FeatureGate from './components/FeatureGate';
import FileUploader from './components/FileUploader';
import EmailProtection from './components/EmailProtection';
import PrivacyCheckupWeb from './components/PrivacyCheckupWeb';
import EndpointDetectionResponse from './components/EndpointDetectionResponse';
import ScamCheckWeb from './components/ScamCheckWeb';
import EmptyState from './components/EmptyState';
import ChatWidget from './components/ChatWidget';
import SecurityScoreDetails from './components/SecurityScoreDetails';
import LicenseActivation from './components/LicenseActivation';
import LicensePromptModal from './components/LicensePromptModal';
import { hasFeatureAccess, getTierInfo, getUserTier } from './utils/subscriptionTiers';
import {
  Shield,
  Scan,
  Globe,
  Brain,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Activity,
  Lock,
  Eye,
  Zap,
  TrendingUp,
  Wifi,
  Server,
  FileText,
  Link,
  Search,
  Mic,
  Camera,
  Usb,
  Bluetooth,
  Monitor,
  HardDrive,
  Headphones,
  Radio,
  ShoppingCart,
  CreditCard,
  Crown,
  Star,
  Check,
  Mail,
  Inbox,
  MessageSquare,
  Settings,
  Download,
  Upload,
  User,
  Home,
  RefreshCw,
  LogIn,
  X,
  Key,
  Bell,
  Phone,
  Send
} from 'lucide-react';

// Dashboard Header Component
const DashboardHeader = ({ user, onLogout }) => {
  const tierInfo = getTierInfo(user);
  
  return (
    <header className="dashboard-header" style={{
        background: 'rgba(6, 9, 15, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.1)'
      }}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Shield className="h-8 w-8" style={{ color: '#00d4ff' }} />
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse" style={{ background: '#00ff88' }}></div>
            </div>
            <div>
              <h1 className="text-xl font-bold" style={{ color: '#ffffff' }}>CyberGuard Global AI</h1>
              <div className="text-xs" style={{ color: '#e2e8f0' }}>Advanced Cybersecurity Dashboard</div>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <RouterLink
              to="/quarantine"
              className="transition-colors flex items-center"
              style={{ color: '#e2e8f0' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#00d4ff'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#e2e8f0'}
            >
              <Shield className="h-5 w-5 inline mr-2" />
              Threat Quarantine
            </RouterLink>
            {user?.role === 'admin' && (
              <RouterLink
                to="/admin/licenses"
                className="transition-colors flex items-center"
                style={{ color: '#e2e8f0' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ffd700'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#e2e8f0'}
              >
                <Key className="h-5 w-5 inline mr-2" />
                Admin Dashboard
              </RouterLink>
            )}
            <a href="/" className="transition-colors" style={{ color: '#e2e8f0' }}
               onMouseEnter={(e) => e.target.style.color = '#00d4ff'}
               onMouseLeave={(e) => e.target.style.color = '#e2e8f0'}>
              <Home className="h-5 w-5 inline mr-2" />
              Back to Home
            </a>
          </nav>
          
          {/* User Actions */}
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm font-medium" style={{ color: '#ffffff' }}>{user?.email}</div>
              <div className="text-xs" style={{ color: '#00d4ff' }}>
                {tierInfo.name} Plan
              </div>
            </div>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              className="transition-all"
              style={{
                border: '1px solid rgba(0, 212, 255, 0.3)',
                color: '#00d4ff',
                background: 'rgba(0, 212, 255, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <User className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

function App() {
  const { user, logout, deleteAccount } = useAuth();

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to permanently delete your account? This cannot be undone.')) {
      if (window.confirm('Final confirmation: all your data will be permanently deleted. Continue?')) {
        deleteAccount().then(result => {
          if (!result.success) {
            alert('Account deletion failed. Please contact support@cyberguardglobal.com');
          }
        });
      }
    }
  };
  const [activeTab, setActiveTab] = useState('scanner');
  const [securityScore, setSecurityScore] = useState(100); // Start at 100, calculated from real data
  const [isScanning, setIsScanning] = useState(false);
  const [scanFile, setScanFile] = useState('');
  const [scanUrl, setScanUrl] = useState('');
  const [scanIp, setScanIp] = useState('');
  const [scanResults, setScanResults] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [bulkScanResults, setBulkScanResults] = useState([]);
  
  // VPN State Management
  const [vpnConnected, setVpnConnected] = useState(false);
  const [vpnConnecting, setVpnConnecting] = useState(false);
  const [selectedVpnServer, setSelectedVpnServer] = useState(null);

  // License activation check
  const [showLicensePrompt, setShowLicensePrompt] = useState(false);
  const [licenseChecked, setLicenseChecked] = useState(false);

  // Platform-wide stats (fetched from API - combines base + real data + daily growth)
  const [platformStats, setPlatformStats] = useState({
    totalThreatsBlocked: 35000,  // Initial value before API loads
    totalDevicesProtected: 1500, // Initial value before API loads
    avgScanTime: '1.2s',
    detectionRate: '99.7%'
  });

  // Fetch platform stats from API
  useEffect(() => {
    const fetchPlatformStats = async () => {
      try {
        const API_URL = getApiUrl();
        const response = await fetch(`${API_URL}/api/platform-stats`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.stats) {
            setPlatformStats({
              totalThreatsBlocked: data.stats.totalThreatsBlocked,
              totalDevicesProtected: data.stats.totalDevicesProtected,
              avgScanTime: data.stats.avgScanTime || '1.2s',
              detectionRate: data.stats.detectionRate || '99.7%'
            });
          }
        }
      } catch (err) {
        console.log('Platform stats fetch error:', err);
        // Keep default values on error
      }
    };

    fetchPlatformStats();
    // Refresh stats every 5 minutes to pick up new activity
    const interval = setInterval(fetchPlatformStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // User's personal stats (starts at zero, grows with actual usage)
  const [userStats, setUserStats] = useState({
    scansPerformed: 0,
    threatsDetected: 0,
    emailsScanned: 0,
    emailThreatsBlocked: 0,
    lastScanDate: null
  });

  // Threat Notification Settings - visible on main dashboard
  const [notificationSettings, setNotificationSettings] = useState({
    emailAlerts: true,
    smsNotifications: false,
    desktopNotifications: true,
    emergencyAlerts: true
  });
  const [notificationContact, setNotificationContact] = useState({
    email_address: '',
    phone_number: ''
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(null);

  // Get user's subscription tier info - memoized to prevent new object on every render
  const tierInfo = useMemo(() => getTierInfo(user), [user?.subscription_tier]);
  const userTier = getUserTier(user);

  // Check if user needs to activate license on first login
  useEffect(() => {
    const checkLicenseActivation = async () => {
      if (!user || licenseChecked) return;

      try {
        const API_URL = getApiUrl();
        const token = localStorage.getItem('token');

        const response = await fetch(`${API_URL}/api/license/info`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();

          // TEMPORARY: Disable license prompt for owner access
          // Show license prompt if user doesn't have a license
          // if (!data.has_license) {
          //   setShowLicensePrompt(true);
          // }
        }
      } catch (err) {
        console.log('License check error:', err);
      } finally {
        setLicenseChecked(true);
      }
    };

    checkLicenseActivation();
  }, [user, licenseChecked]);

  // Handle successful license activation
  const handleLicenseActivationSuccess = (data) => {
    setShowLicensePrompt(false);
    // If user skipped, don't reload - just close the modal
    if (data?.skip) {
      return;
    }
    // Otherwise reload to refresh license data
    window.location.reload();
  };

  // Fetch live data from backend (user stats, EDR stats, threats, endpoints)
  useEffect(() => {
    const fetchLiveData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const API_URL = getApiUrl();

        // Fetch user stats (scans, threats from database)
        try {
          const userStatsRes = await fetch(`${API_URL}/api/user/stats`, { headers });
          if (userStatsRes.ok) {
            const statsData = await userStatsRes.json();
            if (statsData.success && statsData.stats) {
              setUserStats(prev => ({
                ...prev,
                scansPerformed: statsData.stats.scansPerformed || 0,
                threatsDetected: statsData.stats.threatsDetected || 0,
                emailsScanned: statsData.stats.emailsScanned || 0,
                emailThreatsBlocked: statsData.stats.emailThreatsBlocked || 0,
                lastScanDate: statsData.stats.lastScanDate
              }));
            }
          }
        } catch (err) {
          console.log('User stats fetch (optional):', err.message);
        }

        // Fetch EDR status and threats (supplements user stats with real-time EDR data)
        try {
          const [edrStatusRes, edrThreatsRes, edrEndpointsRes] = await Promise.all([
            fetch(`${API_URL}/api/edr/status`, { headers }),
            fetch(`${API_URL}/api/edr/threats?limit=10`, { headers }),
            fetch(`${API_URL}/api/edr/endpoints`, { headers })
          ]);

          if (edrStatusRes.ok) {
            const edrStatus = await edrStatusRes.json();
            if (edrStatus.threat_level && edrThreatsRes.ok) {
              const threatsData = await edrThreatsRes.json();
              const endpoints = await edrEndpointsRes.json();

              // Add EDR threats to user stats (if any active threats)
              const edrThreats = threatsData.threats?.length || 0;
              if (edrThreats > 0) {
                setUserStats(prev => ({
                  ...prev,
                  threatsDetected: prev.threatsDetected + edrThreats,
                  endpointsMonitored: endpoints.endpoints?.length || 0
                }));
              }
            }
          }
        } catch (err) {
          console.log('EDR data fetch (optional):', err.message);
        }
      } catch (err) {
        console.log('Live data fetch error (non-critical):', err);
      }
    };

    // Fetch on mount and then every 30 seconds
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch real-time security score from backend API
  useEffect(() => {
    const fetchSecurityScore = async () => {
      try {
        const API_URL = getApiUrl();
        const token = localStorage.getItem('token');

        if (!token) return;

        const response = await fetch(`${API_URL}/api/security/score`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSecurityScore(data.score);
          } else {
            // Fallback to default score if calculation fails
            setSecurityScore(50);
          }
        }
      } catch (error) {
        console.error('Failed to fetch security score:', error);
        // Keep current score or set to default
        setSecurityScore(prev => prev || 50);
      }
    };

    // Fetch on mount and whenever data changes
    fetchSecurityScore();

    // Refresh security score every 30 seconds
    const interval = setInterval(fetchSecurityScore, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load notification settings on mount
  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token || !user?.id) return;

        const API_URL = getApiUrl();
        const response = await fetch(`${API_URL}/api/notifications/settings/${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            setNotificationSettings({
              emailAlerts: data.settings.email_alerts ?? true,
              smsNotifications: data.settings.sms_notifications ?? false,
              desktopNotifications: data.settings.desktop_notifications ?? true,
              emergencyAlerts: data.settings.emergency_alerts ?? true
            });
            setNotificationContact({
              email_address: data.settings.email_address || user?.email || '',
              phone_number: data.settings.phone_number || ''
            });
          }
        }
      } catch (error) {
        console.error('Failed to load notification settings:', error);
      }
    };

    loadNotificationSettings();
  }, [user?.id, user?.email]);

  // Save notification settings
  const saveNotificationSettings = async () => {
    setNotificationSaving(true);
    setNotificationStatus(null);

    try {
      const token = localStorage.getItem('token');
      const API_URL = getApiUrl();

      const settings = {
        email_alerts: notificationSettings.emailAlerts,
        sms_notifications: notificationSettings.smsNotifications,
        desktop_notifications: notificationSettings.desktopNotifications,
        emergency_alerts: notificationSettings.emergencyAlerts,
        email_address: notificationContact.email_address,
        phone_number: notificationContact.phone_number
      };

      const response = await fetch(`${API_URL}/api/notifications/settings/${user?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (data.success) {
        setNotificationStatus({ type: 'success', message: 'Settings saved successfully!' });
      } else {
        setNotificationStatus({ type: 'error', message: data.error || 'Failed to save settings' });
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      setNotificationStatus({ type: 'error', message: 'Error saving settings. Please try again.' });
    } finally {
      setNotificationSaving(false);
      // Clear status after 3 seconds
      setTimeout(() => setNotificationStatus(null), 3000);
    }
  };

  // Send test notification
  const sendTestNotification = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = getApiUrl();

      const response = await fetch(`${API_URL}/api/notifications/test/${user?.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ type })
      });

      const data = await response.json();

      if (data.success) {
        setNotificationStatus({ type: 'success', message: `Test ${type} sent successfully!` });
      } else {
        setNotificationStatus({ type: 'error', message: data.error || `Failed to send test ${type}` });
      }
    } catch (error) {
      console.error(`Error sending test ${type}:`, error);
      setNotificationStatus({ type: 'error', message: `Error sending test ${type}. Please try again.` });
    } finally {
      setTimeout(() => setNotificationStatus(null), 3000);
    }
  };

  // Handle file upload functionality
  const handleFileSelect = (files) => {
    setSelectedFiles(files);
  };

  const handleBulkScan = async (files) => {
    setIsScanning(true);
    const results = [];
    
    for (const fileInfo of files) {
      try {
        const response = await fetch(`${getApiUrl()}/api/scan/file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            filename: fileInfo.name, 
            content: btoa(`File content for ${fileInfo.name}`),
            size: fileInfo.file.size
          })
        });
        
        const result = await response.json();
        results.push({
          ...fileInfo,
          scanResult: {
            status: result.status || 'unknown',
            engines: result.engines_total || 0,
            threats: result.engines_detected || 0,
            scan_id: result.scan_id
          }
        });
        
        console.log('Bulk scan result for', fileInfo.name, ':', result);
      } catch (error) {
        console.error('Error scanning', fileInfo.name, ':', error);
        results.push({
          ...fileInfo,
          scanResult: {
            status: 'error',
            error: 'Scan failed'
          }
        });
      }
    }
    
    setBulkScanResults(results);
    setIsScanning(false);
    
    // Update user's personal stats with real data
    if (results.length > 0) {
      const threatsFound = results.reduce((sum, r) => sum + (r.scanResult.threats || 0), 0);
      setUserStats(prev => ({
        ...prev,
        scansPerformed: prev.scansPerformed + results.length,
        threatsDetected: prev.threatsDetected + threatsFound,
        lastScanDate: new Date()
      }));
      
      const latestResult = results[results.length - 1];
      setScanResults({
        type: 'file',
        name: latestResult.name,
        status: latestResult.scanResult.status,
        engines: latestResult.scanResult.engines,
        threats: latestResult.scanResult.threats,
        scan_id: latestResult.scanResult.scan_id
      });
    }
  };

  const handleFileScan = async () => {
    if (!scanFile) return;
    setIsScanning(true);
    
    try {
      const response = await fetch(`${getApiUrl()}/api/scan/file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: scanFile, content: btoa(`Dummy file content for ${scanFile}`) })
      });
      
      const result = await response.json();
      setScanResults({
        type: 'file',
        name: scanFile,
        status: result.status || 'unknown',
        engines: result.engines_total || 0,
        threats: result.engines_detected || 0,
        scan_id: result.scan_id,
        message: result.message
      });
    } catch (error) {
      console.error('File scan error:', error);
      setScanResults({
        type: 'file',
        name: scanFile,
        status: 'error',
        error: 'Scan failed. Please try again.'
      });
    }
    
    setIsScanning(false);
  };

  const handleUrlScan = async () => {
    if (!scanUrl) return;
    setIsScanning(true);
    
    try {
      const response = await fetch(`${getApiUrl()}/api/scan/url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: scanUrl })
      });
      
      const result = await response.json();
      setScanResults({
        type: 'url',
        name: scanUrl,
        status: result.status || 'unknown',
        engines: result.engines_total || 0,
        threats: result.engines_detected || 0,
        scan_id: result.scan_id,
        sources: result.sources_checked
      });
    } catch (error) {
      setScanResults({
        type: 'url',
        name: scanUrl,
        status: 'error',
        error: 'Scan failed. Please try again.'
      });
    }
    
    setIsScanning(false);
  };

  const handleIpCheck = async () => {
    if (!scanIp) return;
    setIsScanning(true);
    
    try {
      const response = await fetch(`${getApiUrl()}/api/check/ip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip: scanIp })
      });
      
      const result = await response.json();
      setScanResults({
        type: 'ip',
        name: scanIp,
        status: result.status || 'unknown',
        engines: 1,
        threats: result.confidence > 25 ? 1 : 0,
        scan_id: result.scan_id,
        confidence: result.confidence,
        country: result.country,
        abuse_reports: result.abuse_reports,
        additional_info: result.additional_info
      });
    } catch (error) {
      setScanResults({
        type: 'ip',
        name: scanIp,
        status: 'error',
        error: 'Check failed. Please try again.'
      });
    }
    
    setIsScanning(false);
  };

  return (
    <div className="App min-h-screen" style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #06090f 0%, #0a0f1c 50%, #1a1f35 100%)',
        overflow: 'hidden'
      }}>
      {/* Background Image Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/cyber-background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: 0.15,
        zIndex: 0,
        pointerEvents: 'none'
      }}></div>
      
      {/* Overlay gradient to blend background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(ellipse at center, rgba(6, 9, 15, 0.3) 0%, rgba(6, 9, 15, 0.7) 50%, rgba(6, 9, 15, 0.9) 100%)',
        zIndex: 0,
        pointerEvents: 'none'
      }}></div>
      
      {/* Content wrapper with higher z-index */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Dashboard Header */}
        <DashboardHeader user={user} onLogout={logout} />
      
      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="p-6 rounded-2xl transition-all duration-300" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Activity className="h-5 w-5" style={{ color: '#00ff88' }} />
                  <span className="text-sm font-medium" style={{ color: '#00ff88' }}>System Status: All Protected</span>
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>
                  Welcome back, {user?.name || user?.email?.split('@')[0] || 'User'}!
                </h2>
                <p style={{ color: '#e2e8f0' }}>
                  Current Plan: <span className="font-semibold" style={{ color: '#00d4ff' }}>{tierInfo.name}</span>
                </p>
              </div>
              <div className="text-right">
                <div className="text-sm" style={{ color: '#e2e8f0' }}>Security Score</div>
                <div className="text-3xl font-bold" style={{ color: '#00ff88' }}>{securityScore}/100</div>
              </div>
            </div>
            
            {/* Real-time Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 0 40px rgba(0, 255, 136, 0.5)';
                  e.currentTarget.style.borderColor = '#00ffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(0, 212, 255, 0.2)';
                }}>
                <div className="text-2xl font-bold" style={{ color: '#00d4ff' }}>{platformStats.totalDevicesProtected.toLocaleString()}</div>
                <div className="text-xs" style={{ color: '#94a3b8' }}>Platform: Devices Protected</div>
              </div>
              <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#ff6b35' }}>{platformStats.totalThreatsBlocked.toLocaleString()}</div>
                <div className="text-xs" style={{ color: '#94a3b8' }}>Platform: Threats Blocked</div>
              </div>
              <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#00ff88' }}>{userStats.scansPerformed}</div>
                <div className="text-xs" style={{ color: '#94a3b8' }}>Your Scans</div>
              </div>
              <div className="text-center p-4 rounded-xl transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)'
                }}>
                <div className="text-2xl font-bold" style={{ color: '#8b5cf6' }}>{userStats.threatsDetected}</div>
                <div className="text-xs" style={{ color: '#94a3b8' }}>Threats You Found</div>
              </div>
            </div>
          </div>
        </div>

        {/* Threat Quarantine Quick Access */}
        <div className="mb-8">
          <RouterLink to="/quarantine" className="block">
            <div className="p-6 rounded-2xl transition-all duration-300 cursor-pointer" style={{
                background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 0, 136, 0.1) 100%)',
                backdropFilter: 'blur(20px)',
                border: '2px solid rgba(255, 107, 53, 0.3)',
                boxShadow: '0 8px 32px rgba(255, 107, 53, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(255, 107, 53, 0.4)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(255, 107, 53, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.3)';
              }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-4 rounded-xl" style={{
                      background: 'linear-gradient(135deg, #ff6b35 0%, #ff0088 100%)',
                      boxShadow: '0 4px 20px rgba(255, 107, 53, 0.5)'
                    }}>
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-1" style={{ color: '#ffffff' }}>
                      Threat Quarantine Manager
                    </h3>
                    <p className="text-sm" style={{ color: '#e2e8f0' }}>
                      View and manage quarantined threats, review pending actions, and configure protection settings
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-3xl font-bold" style={{ color: '#ff6b35' }}>
                      {userStats.threatsDetected || 0}
                    </div>
                    <div className="text-xs" style={{ color: '#94a3b8' }}>Threats Detected</div>
                  </div>
                  <div className="p-3 rounded-full" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                    <Activity className="h-6 w-6" style={{ color: '#00d4ff' }} />
                  </div>
                </div>
              </div>
            </div>
          </RouterLink>
        </div>

        {/* Threat Notification Settings - Prominent on Main Dashboard */}
        <div className="mb-8">
          <Card className="transition-all duration-300" style={{
              background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.1) 0%, rgba(0, 212, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              border: '2px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '1rem'
            }}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-3 rounded-xl" style={{
                      background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                      boxShadow: '0 4px 20px rgba(0, 102, 255, 0.4)'
                    }}>
                    <Bell className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle style={{ color: '#ffffff' }}>Threat Alert Notifications</CardTitle>
                    <CardDescription style={{ color: '#94a3b8' }}>
                      Get instant alerts when threats are detected via email or SMS
                    </CardDescription>
                  </div>
                </div>
                {notificationStatus && (
                  <div className={`px-3 py-1 rounded-full text-sm ${
                    notificationStatus.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {notificationStatus.message}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center" style={{ color: '#e2e8f0' }}>
                    <Mail className="h-4 w-4 mr-2" style={{ color: '#00d4ff' }} />
                    Contact Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm block mb-1" style={{ color: '#94a3b8' }}>Email Address</label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={notificationContact.email_address}
                        onChange={(e) => setNotificationContact(prev => ({ ...prev, email_address: e.target.value }))}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#ffffff'
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-sm block mb-1" style={{ color: '#94a3b8' }}>
                        <Phone className="h-3 w-3 inline mr-1" />
                        Phone Number for SMS
                      </label>
                      <Input
                        type="tel"
                        placeholder="+1234567890"
                        value={notificationContact.phone_number}
                        onChange={(e) => setNotificationContact(prev => ({ ...prev, phone_number: e.target.value }))}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#ffffff'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Notification Toggles */}
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center" style={{ color: '#e2e8f0' }}>
                    <Settings className="h-4 w-4 mr-2" style={{ color: '#00d4ff' }} />
                    Alert Preferences
                  </h4>
                  <div className="space-y-2">
                    {[
                      { label: 'Email Alerts', key: 'emailAlerts', icon: Mail, desc: 'Receive threat alerts via email' },
                      { label: 'SMS Alerts', key: 'smsNotifications', icon: Phone, desc: 'Get text messages for critical threats' },
                      { label: 'Desktop Notifications', key: 'desktopNotifications', icon: Monitor, desc: 'Browser popup notifications' },
                      { label: 'Emergency Alerts', key: 'emergencyAlerts', icon: AlertTriangle, desc: 'High-priority threat alerts' }
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-2 rounded-lg" style={{
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                        <div className="flex items-center space-x-2">
                          <item.icon className="h-4 w-4" style={{ color: '#00d4ff' }} />
                          <div>
                            <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{item.label}</span>
                            <p className="text-xs" style={{ color: '#64748b' }}>{item.desc}</p>
                          </div>
                        </div>
                        <div
                          className="w-10 h-5 rounded-full transition-colors cursor-pointer flex items-center"
                          style={{
                            backgroundColor: notificationSettings[item.key] ? '#00d4ff' : '#64748b',
                            padding: '2px'
                          }}
                          onClick={() => setNotificationSettings(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                        >
                          <div className={`w-4 h-4 rounded-full transition-transform bg-white ${
                            notificationSettings[item.key] ? 'translate-x-5' : 'translate-x-0'
                          }`}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                <Button
                  onClick={saveNotificationSettings}
                  disabled={notificationSaving}
                  style={{
                    background: 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)',
                    color: '#ffffff',
                    border: 'none'
                  }}
                >
                  {notificationSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => sendTestNotification('email')}
                  variant="outline"
                  disabled={!notificationContact.email_address}
                  style={{
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    color: '#00d4ff',
                    background: 'rgba(0, 212, 255, 0.1)'
                  }}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Test Email
                </Button>
                <Button
                  onClick={() => sendTestNotification('sms')}
                  variant="outline"
                  disabled={!notificationContact.phone_number}
                  style={{
                    border: '1px solid rgba(0, 212, 255, 0.3)',
                    color: '#00d4ff',
                    background: 'rgba(0, 212, 255, 0.1)'
                  }}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Test SMS
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Dashboard Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="p-1" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              borderRadius: '1rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '0.5rem',
              width: '100%'
            }}>
            <TabsTrigger
              value="scanner"
              className="transition-all duration-200"
              style={{
                color: activeTab === 'scanner' ? '#ffffff' : '#e2e8f0',
                background: activeTab === 'scanner' ? 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)' : 'transparent',
                borderRadius: '0.75rem'
              }}
            >
              <Scan className="h-4 w-4 mr-2" />
              Multi-Engine Scanner
            </TabsTrigger>

            {/* Email Protection Tab - Professional and Enterprise only */}
            <TabsTrigger
              value="email-protection" 
              className="transition-all duration-200"
              disabled={!hasFeatureAccess(user, 'email_protection')}
              style={{
                color: activeTab === 'email-protection' ? '#ffffff' : '#e2e8f0',
                background: activeTab === 'email-protection' ? 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)' : 'transparent',
                borderRadius: '0.75rem',
                opacity: !hasFeatureAccess(user, 'email_protection') ? 0.5 : 1
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Protection {!hasFeatureAccess(user, 'email_protection') && <Crown className="h-3 w-3 ml-1" style={{ color: '#ffd700' }} />}
            </TabsTrigger>
            
            {/* Anti-Surveillance Tab - Professional and Enterprise only */}
            <TabsTrigger 
              value="anti-surveillance" 
              className="transition-all duration-200"
              disabled={!hasFeatureAccess(user, 'anti_surveillance')}
              style={{
                color: activeTab === 'anti-surveillance' ? '#ffffff' : '#e2e8f0',
                background: activeTab === 'anti-surveillance' ? 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)' : 'transparent',
                borderRadius: '0.75rem',
                opacity: !hasFeatureAccess(user, 'anti_surveillance') ? 0.5 : 1
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              Privacy Checkup {!hasFeatureAccess(user, 'anti_surveillance') && <Crown className="h-3 w-3 ml-1" style={{ color: '#ffd700' }} />}
            </TabsTrigger>
            
            {/* EDR Tab - Basic and above */}
            <TabsTrigger 
              value="edr" 
              className="transition-all duration-200"
              disabled={!hasFeatureAccess(user, 'multi_engine_scanner')}
              style={{
                color: activeTab === 'edr' ? '#ffffff' : '#e2e8f0',
                background: activeTab === 'edr' ? 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)' : 'transparent',
                borderRadius: '0.75rem',
                opacity: !hasFeatureAccess(user, 'multi_engine_scanner') ? 0.5 : 1
              }}
            >
              <Shield className="h-4 w-4 mr-2" />
              Desktop Protection {!hasFeatureAccess(user, 'multi_engine_scanner') && <Crown className="h-3 w-3 ml-1" style={{ color: '#ffd700' }} />}
            </TabsTrigger>
            
            <TabsTrigger 
              value="ai-protection" 
              className="transition-all duration-200"
              style={{
                color: activeTab === 'ai-protection' ? '#ffffff' : '#e2e8f0',
                background: activeTab === 'ai-protection' ? 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)' : 'transparent',
                borderRadius: '0.75rem'
              }}
            >
              <Brain className="h-4 w-4 mr-2" />
              Scam Check
            </TabsTrigger>
            
            <TabsTrigger
              value="account"
              className="transition-all duration-200"
              style={{
                color: activeTab === 'account' ? '#ffffff' : '#e2e8f0',
                background: activeTab === 'account' ? 'linear-gradient(135deg, #0066ff 0%, #00d4ff 100%)' : 'transparent',
                borderRadius: '0.75rem'
              }}
            >
              <User className="h-4 w-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Multi-Engine Scanner Tab */}
          <TabsContent value="scanner" className="space-y-6">
            {/* Security Score Details - Shows what's affecting the score */}
            <SecurityScoreDetails user={user} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Advanced File Scanner */}
              <Card className="lg:col-span-3 transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  borderRadius: '1.5rem',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <FileText className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    Advanced Multi-Engine File Scanner
                  </CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>Upload and scan files using 70+ antivirus engines with advanced threat detection</CardDescription>
                </CardHeader>
                <CardContent>
                  <FileUploader
                    onFileSelect={handleFileSelect}
                    onScanStart={handleBulkScan}
                    isScanning={isScanning}
                    maxFileSize={tierInfo.name === 'Enterprise Premium Guard' ? Infinity : tierInfo.name === 'Professional Guard' ? 500 * 1024 * 1024 : 100 * 1024 * 1024}
                  />
                  
                  {/* Quick scan options */}
                  <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    <h4 className="font-medium mb-4" style={{ color: '#ffffff' }}>Quick Scan Options</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex space-x-2">
                        <Input
                          placeholder="Enter filename to scan..."
                          value={scanFile}
                          onChange={(e) => setScanFile(e.target.value)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '1rem',
                            color: '#ffffff'
                          }}
                        />
                        <Button 
                          onClick={handleFileScan}
                          disabled={isScanning || !scanFile}
                          size="sm"
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                        >
                          {isScanning ? <Search className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>

            {/* Quick Network Scanners */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* URL Scanner */}
              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  borderRadius: '1.5rem'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <Link className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    URL Scanner
                  </CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>Check URLs for phishing and malware</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Enter URL to scan..."
                      value={scanUrl}
                      onChange={(e) => setScanUrl(e.target.value)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '1rem',
                        color: '#ffffff'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#00d4ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(0, 212, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <Button 
                      onClick={handleUrlScan}
                      disabled={isScanning || !scanUrl}
                      className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                    >
                      {isScanning ? <Search className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* IP Scanner */}
              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  borderRadius: '1.5rem'
                }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <Globe className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    IP Checker
                  </CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>Check IP reputation and geolocation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Enter IP address..."
                      value={scanIp}
                      onChange={(e) => setScanIp(e.target.value)}
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '1rem',
                        color: '#ffffff'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#00d4ff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(0, 212, 255, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <Button 
                      onClick={handleIpCheck}
                      disabled={isScanning || !scanIp}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    >
                      {isScanning ? <Search className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Scan Results */}
            {scanResults && (
              <Card className="transition-all duration-300" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  borderRadius: '1.5rem',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                      {scanResults.status === 'clean' ? (
                        <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                      ) : (
                        <AlertTriangle className={`h-5 w-5 mr-2 ${['unknown', 'error', 'scanning'].includes(scanResults.status) ? 'text-yellow-500' : 'text-red-600'}`} />
                      )}
                      Check Results
                    </CardTitle>
                    <Badge variant="outline" className="border-cyan-500/50 text-cyan-300">
                      {scanResults.type === 'ip' ? 'Source: AbuseIPDB' : scanResults.type === 'url' ? 'Sources: Google Safe Browsing, PhishTank, VirusTotal' : 'Source: VirusTotal'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">{scanResults.type === 'file' ? 'File' : scanResults.type === 'url' ? 'URL' : 'IP Address'}:</span>
                    <span className="text-gray-800 font-mono text-sm">{scanResults.name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Status:</span>
                      <Badge className={scanResults.status === 'clean' ? 'bg-green-100 text-green-700 border-green-200' : ['unknown', 'error', 'scanning'].includes(scanResults.status) ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200'}>
                        {({ clean: 'NO THREATS FOUND', unknown: "COULDN'T CHECK", error: "COULDN'T CHECK", scanning: 'SUBMITTED FOR ANALYSIS' })[scanResults.status] || 'THREAT DETECTED'}
                      </Badge>
                    </div>
                    
                    {scanResults.type === 'ip' ? (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Abuse Confidence:</span>
                          <span className={scanResults.confidence > 25 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                            {typeof scanResults.confidence === 'number' ? `${scanResults.confidence}%` : 'Not available'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Country:</span>
                          <span className="text-blue-600 font-bold">{scanResults.country}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Engines Checked:</span>
                          <span className="text-blue-600 font-bold">{scanResults.engines}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700">Threats Detected:</span>
                          <span className={scanResults.threats > 0 ? 'text-red-600 font-bold' : 'text-green-600 font-bold'}>
                            {scanResults.threats}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Email Protection Tab */}
          <TabsContent value="email-protection">
            <FeatureGate user={user} feature="email_protection">
              <EmailProtection user={user} tierInfo={tierInfo} />
            </FeatureGate>
          </TabsContent>

          {/* Anti-Surveillance Tab */}
          <TabsContent value="anti-surveillance">
            <FeatureGate user={user} feature="anti_surveillance">
              <PrivacyCheckupWeb />
            </FeatureGate>
          </TabsContent>

          {/* EDR Tab */}
          <TabsContent value="edr">
            <FeatureGate user={user} feature="multi_engine_scanner">
              <EndpointDetectionResponse user={user} tierInfo={tierInfo} />
            </FeatureGate>
          </TabsContent>

          {/* AI Protection Tab */}
          <TabsContent value="ai-protection" className="space-y-6">
            <ScamCheckWeb />
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            <div className="max-w-2xl mx-auto space-y-6">
              {/* Profile Info */}
              <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '1.5rem' }}>
                <CardHeader>
                  <CardTitle className="flex items-center" style={{ color: '#ffffff' }}>
                    <User className="h-5 w-5 mr-2" style={{ color: '#00d4ff' }} />
                    Account Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div style={{ borderBottom: '1px solid rgba(0,212,255,0.1)', paddingBottom: '1rem' }}>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Email</p>
                    <p style={{ color: '#ffffff', fontSize: '1rem' }}>{user?.email}</p>
                  </div>
                  <div>
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Plan</p>
                    <p style={{ color: '#00d4ff', fontSize: '1rem', fontWeight: '600' }}>{getTierInfo(user).name}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Danger Zone */}
              <Card style={{ background: 'rgba(255,59,48,0.05)', border: '1px solid rgba(255,59,48,0.3)', borderRadius: '1.5rem' }}>
                <CardHeader>
                  <CardTitle style={{ color: '#ff3b30' }}>Danger Zone</CardTitle>
                  <CardDescription style={{ color: '#94a3b8' }}>
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleDeleteAccount}
                    style={{
                      background: 'transparent',
                      border: '1px solid #ff3b30',
                      color: '#ff3b30',
                      borderRadius: '0.75rem',
                      padding: '0.75rem 1.5rem'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,59,48,0.15)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    Delete My Account
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      </div>

      {/* AI Chat Widget - Available globally */}
      {user && <ChatWidget />}

      {/* License Activation Prompt Modal */}
      {showLicensePrompt && user && (
        <LicensePromptModal
          user={user}
          onActivationSuccess={handleLicenseActivationSuccess}
        />
      )}
    </div>
  );
}

export default App;